/**
 * Kontextsensitive Hilfe-Links: Ordnet der aktuellen App-Route die passende
 * Seite der Online-Dokumentation zu (docs.kursorganizer.com).
 *
 * Die Slugs entsprechen den Dateipfaden im ko-docs-Repository ohne Endung,
 * z.B. `docs/buchungen/buchung-erstellen.md` -> `buchungen/buchung-erstellen`.
 *
 * Gegen Link-Rot sichern zwei Checks in beide Richtungen ab:
 * - `npm run check:help-links` (hier, woechentlich) prueft jeden Slug per HTTP
 *   gegen die Live-Doku.
 * - triias/ko-docs prueft bei jedem Doku-PR, dass die verlinkten Seiten noch
 *   existieren.
 *
 * WICHTIG: Wer diese Map aendert, pflegt `linked-help-slugs.json` in
 * triias/ko-docs mit — sonst schuetzt der dortige Check die falsche Liste.
 */

export const DOCS_BASE_URL = "https://docs.kursorganizer.com/";

/** Ziel, wenn keine Route passt. Immer erreichbar, nie ein toter Link. */
export const HELP_FALLBACK_SLUG = "erste-schritte/schnellstart";

/**
 * App-Pfad ohne Mandanten-Segment -> Doku-Slug.
 *
 * Bei mehreren Treffern gewinnt der laengste Schluessel, deshalb duerfen
 * Bereichs-Eintraege (`/payments`) neben Detail-Eintraegen (`/payments/refunds`)
 * stehen. Gematcht wird auf Segmentgrenzen: `/courses/coursedetails` greift
 * deshalb NICHT fuer `/courses/coursedetailsrefactored` — solche Routen
 * brauchen einen eigenen Eintrag.
 *
 * Aufgenommen sind nur Bereiche, die tatsaechlich ueber `LayoutStandard`
 * laufen und den Hilfe-Link damit anzeigen. helpLinks.routes.test.ts prueft
 * das gegen die URL-Konstanten und schlaegt bei neuen Routen fehl.
 */
export const HELP_MAP: Record<string, string> = {
    // erste Schritte
    "/dashboard": "erste-schritte/dashboard",

    // Buchungen
    "/bookings/create": "buchungen/buchung-erstellen",
    "/bookings/edit": "buchungen/buchung-bearbeiten",
    "/bookings/courseList": "buchungen/buchung-erstellen",
    "/bookings/bookings/list": "buchungen/buchung-bearbeiten",
    // Abrechnungsdetails einer bestehenden Buchung, keine Neuanlage.
    "/bookings/settlementDetails": "rechnungen",
    "/bookings/lessonBookings/settlementDetails": "rechnungen",
    "/bookings/lessonBookings": "buchungen/einzelbuchung",
    "/bookings": "buchungen/buchung-erstellen",
    "/withdrawals": "buchungen/widerrufe",

    // Kunden
    "/customers/attendees": "kunden/teilnehmer",
    "/customers/bookers": "kunden/bucher",
    "/customers/duplicates": "kunden/duplikate",
    "/customers": "kunden/teilnehmer",
    "/interest-lists": "kunden/interessenten-listen",

    // Kursverwaltung
    "/courses/types": "kursverwaltung/kursvorlagen",
    "/courses/categories": "kursverwaltung/kursvorlagen",
    "/courses/edit": "kursverwaltung/kurs-bearbeiten",
    "/courses/coursedetails": "kursverwaltung/kurs-bearbeiten",
    // Eigener Eintrag: die refaktorierte Detailroute ist ein anderes Segment
    // und muss dasselbe Ziel liefern wie die klassische.
    "/courses/coursedetailsrefactored": "kursverwaltung/kurs-bearbeiten",
    "/courses/prerequisite": "einstellungen/teilnahmevoraussetzungen",
    "/courses/bulkSend": "kommunikation/emails",
    "/courses": "kursverwaltung/kurs-erstellen",
    "/lessonsToday": "kursverwaltung/kurskalender",

    // Standorte
    "/locations": "standorte/standort-verwalten",

    // Abrechnung und Zahlungsverkehr
    // Die Doku-Seite rechnungen/rechnungen.md ist zugleich die Startseite ihres
    // Verzeichnisses, ihr Slug lautet deshalb nur "rechnungen".
    "/invoice": "rechnungen",
    "/payments/refunds": "rechnungen/mollie-erstattungen",
    "/payments/mollie": "rechnungen/mollie-zahlungen",
    "/payments/chargebacks": "rechnungen/mollie-rueckbuchungen",
    "/payments/import-history": "rechnungen/kontoauszug-import",
    "/payments/bank-import": "rechnungen/bankabgleich",
    "/payments": "rechnungen/zahlungen",
    "/mollie-retry-queue": "rechnungen/mollie-zahlungen",

    // Kommunikation
    "/templates": "kommunikation/vorlagen",
    "/notifications": "kommunikation/versandhistorie",
    "/newsletter": "kommunikation/newsletter",

    // Personal
    "/staff/billingStatus": "personal/kursleiterabrechnung",
    "/staff": "personal/kursleiter",
    "/staffLogin": "personal/kursleiter",
    "/mybookings": "personal/meine-buchungen",
    // Regionalleitung hat noch keine eigene Doku-Seite. Bewusst allgemeine
    // Hilfe statt einer inhaltlich falschen Zuordnung.
    "/areaManager": HELP_FALLBACK_SLUG,

    // Einstellungen
    "/settings/enduserapp": "kunden/kundenportal",
    "/settings/ki-assistent": "einstellungen/ki-assistent",
    "/settings/data-export": "einstellungen/daten-export",
    "/settings/invoice-export": "einstellungen/daten-export",
    "/settings": "einstellungen/organisation",
    "/badges": "einstellungen/badges",
    "/certificates": "einstellungen/urkunden",

    // Hilfe-Center: Einstieg in die Doku, deshalb die Startseite.
    "/help": HELP_FALLBACK_SLUG,
};

/** Absteigend nach Laenge, damit der spezifischste Eintrag zuerst greift. */
const SORTED_KEYS = Object.keys(HELP_MAP).sort((a, b) => b.length - a.length);

/**
 * Entfernt das fuehrende Mandanten-Segment aus dem Pfad. Bewusst ueber ein
 * Regex statt ueber URL_BASE, damit die Zuordnung auch dann stimmt, wenn die
 * Mandanten-ID zum Zeitpunkt des Renderns noch nicht aufgeloest ist.
 */
const stripTenantSegment = (pathname: string): string => pathname.replace(/^\/[^/]+/, "");

/** Tracking-Wert fuer Routen ohne Mapping-Eintrag. */
export const HELP_TRACKING_UNMAPPED = "unmapped";

/**
 * Liefert den passenden HELP_MAP-Schluessel oder undefined.
 * Der Rueckgabewert ist immer ein statischer Wert aus der Allowlist oben,
 * niemals der aufgerufene Pfad.
 */
const matchedKeyFor = (pathname: string): string | undefined => {
    const path = stripTenantSegment(pathname);

    return SORTED_KEYS.find((key) => path === key || path.startsWith(`${key}/`));
};

export const helpSlugFor = (pathname: string): string => {
    const key = matchedKeyFor(pathname);

    return key ? HELP_MAP[key] : HELP_FALLBACK_SLUG;
};

/**
 * Statischer Tracking-Schluessel der Route. Bewusst der gematchte HELP_MAP-
 * Eintrag und nicht der reale Pfad: Routen wie
 * `/customers/bookers/details/<booker-id>` enthalten Datensatz-IDs, die den
 * Doku-Host nichts angehen und die Auswertung unbrauchbar zerfasern wuerden.
 */
export const helpTrackingKeyFor = (pathname: string): string => matchedKeyFor(pathname) ?? HELP_TRACKING_UNMAPPED;

/**
 * Vollstaendige Doku-URL zur aktuellen Route. Der utm-Parameter macht in der
 * Doku-Statistik sichtbar, aus welchem App-Bereich Hilfe gesucht wird.
 */
export const helpUrlFor = (pathname: string): string => {
    const slug = helpSlugFor(pathname);
    const trackingKey = encodeURIComponent(helpTrackingKeyFor(pathname));

    return `${DOCS_BASE_URL}${slug}?utm_source=app&utm_content=${trackingKey}`;
};
